<template>
  <ChartCard
    :data-widget-id="widgetId"
    @mouseenter="handleMouseEnter"
  >
    <ChartHeader
      :label="itemLabel"
      :tooltip-key="tooltipKey"
      class="mb-5"
    >
      <template #rightSide>
        <RequisitionReportWidgetCommentsToggle
          class="mr-3"
          :widget-id="widgetId"
          :req-id="reqId"
          :req-uuid="reqUuid"
          :screenshot-selector="`[data-widget-id='${widgetId}']`"
          :thread-name="title"
        />
        <ChartDownload
          :download-id="widgetId"
          :file-name="`${projectName}-requisition-report-${widgetId}-${today}`"
        />
      </template>
    </ChartHeader>
    <ChartContent
      :loading="pageLoading"
      type="doughnutChart"
      height="211px"
      :empty-data="noWidgetData"
    >
      <DoughnutChart
        :id="widgetId"
        :data="chartData"
        :options="chartOptions"
        :plugins="plugins"
      />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { useAppLoading } from '@/stores/app/loading';
import { useTracking } from '@/stores/tracking/tracking';
import { useColors } from '@/composables/colors';
import { LegendSpacingPlugin } from '@/api/helpers/insights/chartPlugins';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import DoughnutChart from '@/components/insights/Chart/DoughnutChart.vue';
import { isEmpty } from 'lodash-es';
import { donutChartTooltipPluginConfig, donutChartLegendPluginConfig, donutChartScalesOptions } from '@/api/helpers/insights/commonChartConfigs';
import ChartDownload from '@/components/insights/Chart/ChartDownload.vue';
import { format } from 'date-fns';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import RequisitionReportWidgetCommentsToggle from './RequisitionReportWidgetCommentsToggle.vue';

const widgetId = 'requisition-gender';
const tooltipKey = 'single-req-gender';
const itemLabel = 'What is the gender representation of the candidate pool?';
const plugins = [LegendSpacingPlugin];

defineProps({
  projectName: { type: String, default: '' },
});

const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

const { candidateMaleInferred, candidateOther, candidateFemaleInferred } = useColors();
const colors = [
  candidateMaleInferred.value,
  candidateFemaleInferred.value,
  candidateOther.value,
];

const {
  gender, reqId, reqUuid, title,
} = storeToRefs(useRequisitionReport());

const { pageLoading } = storeToRefs(useAppLoading());

const noWidgetData = computed(() => isEmpty(gender.value)
  || Object.keys(gender.value).every((key) => !gender.value[key]));

const chartData = computed(() => {
  const labelKey = {
    male: 'Male',
    female: 'Female',
    unknown: 'Unknown',
  };
  const genderValue = gender.value || {};
  const keys = Object.keys(genderValue);
  const values = Object.values(genderValue);
  const total = values.reduce((acc, curr) => acc + curr, 0) || 1;

  const filtered = keys
    .map((key, idx) => ({
      label: labelKey[key],
      value: genderValue[key],
      color: colors[idx],
    }))
    .filter((item) => item.value);

  return {
    labels: filtered.map((item) => item.label),
    datasets: [
      {
        data: filtered.map((item) => (item.value / total) * 100),
        backgroundColor: filtered.map((item) => item.color),
        borderWidth: 0,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  aspectRatio: 1,
  plugins: {
    tooltip: donutChartTooltipPluginConfig(chartData, widgetId),
    legend: donutChartLegendPluginConfig(colors),
  },
  scales: donutChartScalesOptions,
}));

const handleMouseEnter = () => {
  useTracking().trackName({
    name: 'Requisition Report: Gender Representation Pie Chart Hovered',
  });
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

:deep(.chart-error-image) {
  margin-top: 30px;
}

:deep(.outer-circle) {
  height: 200px;
  width: 200px;
}

:deep(.inner-circle) {
  height: 100px;
  width: 100px;
  left: 50px;
  top: 50px;
}
</style>
